import React, { useState } from "react";
import { api } from "@/AuthContext";
import { toast } from "sonner";
import { X, Upload, DownloadSimple } from "@phosphor-icons/react";

/**
 * Modal genérico de importación con preview de 2 pasos.
 * Props:
 *  - title:            string, título del modal
 *  - templateUrl:      GET endpoint que devuelve el CSV plantilla (blob)
 *  - templateFilename: nombre para descargar la plantilla
 *  - previewUrl:       POST endpoint que devuelve {total, counts:{new,update,error}, items:[{row,name,status,message?,existing_name?, ...}]}
 *  - importUrl:        POST endpoint que persiste y devuelve {created, updated, errors, total}
 *  - columnsHint:      texto de columnas aceptadas (para el paso 1)
 *  - columns:          [{key, label}] columnas a mostrar en la tabla de preview
 *  - onClose():
 *  - onSuccess():
 *  - testidPrefix:     prefijo para data-testid (ej. "companies-import" o "installations-import")
 */
export default function ImportModal({ title, templateUrl, templateFilename, previewUrl, importUrl, columnsHint, columns, onClose, onSuccess, testidPrefix }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const tid = (s) => `${testidPrefix}-${s}`;

  const downloadTemplate = async () => {
    try {
      const r = await api.get(templateUrl, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = templateFilename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar la plantilla"); }
  };

  const doPreview = async () => {
    if (!file) { toast.error("Selecciona un archivo"); return; }
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(previewUrl, fd, { headers: { "Content-Type": "multipart/form-data" }});
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al leer el archivo");
    } finally { setBusy(false); }
  };

  const confirmImport = async () => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(importUrl, fd, { headers: { "Content-Type": "multipart/form-data" }});
      setResult(r.data);
      const { created, updated, errors } = r.data || {};
      toast.success(`Importación: ${created} nuevas, ${updated} actualizadas${errors?.length ? `, ${errors.length} con error` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al importar");
    } finally { setBusy(false); }
  };

  const resetFlow = () => { setFile(null); setPreview(null); setResult(null); };

  const badge = (s) => {
    const map = {
      new:    { txt: "Nueva",       cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
      update: { txt: "Actualizar",  cls: "bg-amber-100 text-amber-700 border-amber-200" },
      error:  { txt: "Error",       cls: "bg-rose-100 text-rose-700 border-rose-200" },
    };
    const b = map[s] || map.new;
    return <span className={`px-2 py-0.5 text-xs font-semibold rounded border ${b.cls}`}>{b.txt}</span>;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid={tid("modal")}>
      <div className="bg-white rounded-md w-full max-w-4xl border border-neutral-200 anim-fade-up max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <h2 className="font-display text-xl font-bold tracking-tight">
            {title} {preview && !result && <span className="text-sm font-normal text-neutral-500">— Vista previa</span>}
            {result && <span className="text-sm font-normal text-neutral-500">— Resultado</span>}
          </h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid={tid("close-btn")}><X size={20}/></button>
        </div>

        {!preview && !result && (
          <div className="p-6 space-y-4">
            <div className="text-sm text-neutral-600">
              Sube un archivo <strong>.csv</strong> o <strong>.xlsx</strong> con las columnas:
              <code className="block mt-1 px-2 py-1 bg-neutral-100 rounded text-xs">{columnsHint}</code>
            </div>
            <button onClick={downloadTemplate} className="inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-800 font-semibold" data-testid={tid("download-template-btn")}>
              <DownloadSimple size={16}/> Descargar plantilla CSV
            </button>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Archivo (.csv o .xlsx)</label>
              <input type="file" accept=".csv,.xlsx"
                onChange={(e)=>{ setFile(e.target.files?.[0] || null); }}
                className="w-full text-sm file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-blue-600 file:text-white file:font-semibold hover:file:bg-blue-700"
                data-testid={tid("file-input")}/>
              {file && <div className="mt-2 text-xs text-neutral-500">{file.name} — {(file.size/1024).toFixed(1)} KB</div>}
            </div>
          </div>
        )}

        {preview && !result && (
          <div className="p-6 space-y-4" data-testid={tid("preview")}>
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="px-3 py-1.5 rounded-md bg-neutral-100 border border-neutral-200"><strong>{preview.total}</strong> filas</span>
              <span className="px-3 py-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800"><strong>{preview.counts.new}</strong> nuevas</span>
              <span className="px-3 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800"><strong>{preview.counts.update}</strong> a actualizar</span>
              {preview.counts.error > 0 && <span className="px-3 py-1.5 rounded-md bg-rose-50 border border-rose-200 text-rose-800"><strong>{preview.counts.error}</strong> con error</span>}
            </div>
            <div className="border border-neutral-200 rounded-md overflow-hidden">
              <div className="max-h-[45vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-neutral-600 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">#</th>
                      {columns.map(c => <th key={c.key} className="px-3 py-2 text-left font-semibold">{c.label}</th>)}
                      <th className="px-3 py-2 text-left font-semibold">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {preview.items.map((it) => (
                      <tr key={it.row} className={it.status === "error" ? "bg-rose-50/40" : it.status === "update" ? "bg-amber-50/30" : ""} data-testid={tid(`preview-row-${it.row}`)}>
                        <td className="px-3 py-2 text-neutral-400">{it.row}</td>
                        {columns.map(c => (
                          <td key={c.key} className="px-3 py-2 text-neutral-700">
                            {(it[c.key] || "").toString() || <span className="text-neutral-400">—</span>}
                            {c.key === "name" && it.status === "update" && it.existing_name && it.existing_name !== it.name && (
                              <div className="text-xs text-neutral-500">Existente: {it.existing_name}</div>
                            )}
                          </td>
                        ))}
                        <td className="px-3 py-2">
                          {badge(it.status)}
                          {it.message && <div className="text-xs text-rose-600 mt-1">{it.message}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-xs text-neutral-500">Revisa las filas antes de confirmar. Las filas marcadas como <strong>Error</strong> se omitirán al importar.</p>
          </div>
        )}

        {result && (
          <div className="p-6 space-y-3" data-testid={tid("result")}>
            <div className="p-3 border border-neutral-200 rounded-md bg-neutral-50 text-sm">
              <div><strong>Total filas:</strong> {result.total}</div>
              <div className="text-emerald-700"><strong>Creadas:</strong> {result.created}</div>
              <div className="text-blue-700"><strong>Actualizadas:</strong> {result.updated}</div>
              {result.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-rose-700 font-semibold">Errores ({result.errors.length})</summary>
                  <ul className="mt-1 text-xs text-rose-700 max-h-32 overflow-y-auto">
                    {result.errors.map((e, i) => <li key={i}>Fila {e.row}: {e.error}</li>)}
                  </ul>
                </details>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center gap-2 p-5 border-t border-neutral-200 bg-neutral-50">
          <div>
            {preview && !result && (
              <button onClick={resetFlow} className="text-sm text-neutral-600 hover:text-neutral-800 underline" data-testid={tid("back-btn")}>
                ← Cambiar archivo
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md hover:bg-white" data-testid={tid("cancel-btn")}>
              {result ? "Cerrar" : "Cancelar"}
            </button>
            {!preview && !result && (
              <button onClick={doPreview} disabled={busy || !file} className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid={tid("preview-btn")}>
                {busy ? "Analizando..." : "Vista previa"}
              </button>
            )}
            {preview && !result && (
              <button onClick={confirmImport} disabled={busy || (preview.counts.new + preview.counts.update === 0)} className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid={tid("confirm-btn")}>
                <Upload size={16}/> {busy ? "Importando..." : `Confirmar (${preview.counts.new + preview.counts.update})`}
              </button>
            )}
            {result && (
              <button onClick={onSuccess} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md" data-testid={tid("done-btn")}>
                Ver registros
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
